export type GiftVoucherType = 'value' | 'package' | 'spa_pass';

export type GiftVoucherStatus = 'active' | 'reserved' | 'used' | 'expired' | 'income';

export type GiftVoucherPaymentStatus = 'pending' | 'paid' | 'failed';

export type GiftVoucherPaymentMethod = 'card' | 'bank_transfer';

/**
 * Max length of the buyer's personalized message. Bounded by what still fits
 * inside the recipient panel of the voucher PDF (see gift-voucher-pdf/generate).
 * Enforced on the form, in the store, and again server-side on every write path.
 */
export const MESSAGE_MAX_LENGTH = 60;

/** Buyer's site language, captured at purchase so emails render localized. */
export type GiftVoucherLocale = 'en' | 'ro' | 'hu';

/** Type-specific details stored in the contents jsonb column. */
export interface PackageVoucherContents {
  room_type_id: string;
  room_type_name: string;          // resolved at purchase time so the admin can see it later
  nights: number;
  with_spa: boolean;
  sparkling_wine: boolean;
  sparkling_note?: string | null;
  restaurant_credit_ron: number;   // 0 if not selected
  spa_services: Array<{ key: string; count: number }>;
}

export interface SpaPassVoucherContents {
  entries: 10 | 15;
}

export type GiftVoucherContents =
  | PackageVoucherContents
  | SpaPassVoucherContents
  | Record<string, never>;          // value-based: empty object

export interface GiftVoucher {
  id: string;
  voucher_code: string;
  pyn_balance_id: string | null;

  type: GiftVoucherType;
  purchase_date: string;            // YYYY-MM-DD
  expiry_date: string;              // YYYY-MM-DD

  buyer_name: string;
  buyer_email: string;
  buyer_phone: string;
  buyer_country: string;
  buyer_county: string | null;
  buyer_city: string | null;
  buyer_address: string | null;

  buyer_wants_invoice: boolean;
  company_name: string | null;
  cui: string | null;
  reg_com: string | null;
  company_address: string | null;

  beneficiary_name: string;
  personalized_message: string | null;

  value_ron: number;
  contents: GiftVoucherContents | null;

  status: GiftVoucherStatus;

  payment_status: GiftVoucherPaymentStatus;
  payment_method: GiftVoucherPaymentMethod | null;
  euplatesc_invoice_id: string | null;
  euplatesc_ep_id: string | null;
  code_emailed_at: string | null;

  /** Storage path (private gift-voucher-pdfs bucket) of the generated PDF. */
  pdf_url: string | null;

  locale: GiftVoucherLocale;

  notes: string | null;

  /** Free-text ops comment (admin CMS), distinct from import-metadata `notes`. */
  comment: string | null;

  /** Payment-audit capture, set when an admin confirms an off-platform payment. */
  payment_note: string | null;
  payment_date: string | null;        // YYYY-MM-DD — observed payment date
  payment_marked_by: string | null;   // acting admin email (auto)
  payment_marked_at: string | null;   // server timestamp (auto)

  created_at: string;
  updated_at: string;
}

export interface GiftVoucherInsert {
  voucher_code: string;
  pyn_balance_id?: string | null;

  type: GiftVoucherType;
  purchase_date?: string;
  expiry_date: string;

  buyer_name: string;
  buyer_email: string;
  buyer_phone: string;
  buyer_country?: string;
  buyer_county?: string | null;
  buyer_city?: string | null;
  buyer_address?: string | null;

  buyer_wants_invoice?: boolean;
  company_name?: string | null;
  cui?: string | null;
  reg_com?: string | null;
  company_address?: string | null;

  beneficiary_name: string;
  personalized_message?: string | null;

  value_ron: number;
  contents?: GiftVoucherContents | null;

  status?: GiftVoucherStatus;
  payment_status?: GiftVoucherPaymentStatus;
  payment_method?: GiftVoucherPaymentMethod | null;
  euplatesc_invoice_id?: string | null;
  euplatesc_ep_id?: string | null;
  code_emailed_at?: string | null;

  locale?: GiftVoucherLocale;

  notes?: string | null;
  comment?: string | null;
}

export interface GiftVoucherUpdate {
  pyn_balance_id?: string | null;
  status?: GiftVoucherStatus;
  payment_status?: GiftVoucherPaymentStatus;
  payment_method?: GiftVoucherPaymentMethod | null;
  euplatesc_ep_id?: string | null;
  code_emailed_at?: string | null;
  pdf_url?: string | null;
  notes?: string | null;
  comment?: string | null;
  payment_note?: string | null;
  payment_date?: string | null;
  payment_marked_by?: string | null;
  payment_marked_at?: string | null;
  // Admin can also edit basic identity fields when importing old vouchers
  voucher_code?: string;
  type?: GiftVoucherType;
  purchase_date?: string;
  expiry_date?: string;
  buyer_name?: string;
  buyer_email?: string;
  buyer_phone?: string;
  buyer_country?: string;
  buyer_county?: string | null;
  buyer_city?: string | null;
  buyer_address?: string | null;
  beneficiary_name?: string;
  personalized_message?: string | null;
  value_ron?: number;
  contents?: GiftVoucherContents | null;
}

/** Compute the expiry date for a given voucher type, in YYYY-MM-DD. */
export function computeExpiryDate(
  type: GiftVoucherType,
  purchaseDate: Date = new Date(),
): string {
  // Per product spec the validity window depends on the voucher type:
  //   value (Flexible)        → 12 months from purchase
  //   package (Curated Stay)  → 6 months from purchase
  //   spa_pass (Spa Pass)     → 6 months from purchase
  // (matches the validity copy shown to buyers in messages/*.json)
  const months = type === 'value' ? 12 : 6;
  const d = new Date(purchaseDate);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}
